import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { Form, getIn, withFormik } from 'formik';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import es from '../../../DataLoad/FormOptions';
import { DualList, Icon } from '../../../../components';
import { indexBalancesRequest } from '../../../../requests/balances';
import { indexReliquidationBalancesRequest } from '../../../../requests/reliquidationBalances';
import ReliquidationBalancesFormTable from './ReliquidationBalancesFormTable';
import EmployeeFilterModal from './EmployeeFilterModal';
import './custom.css';

const ReliquidationBalancesForm = ({
  values,
  setFieldValue,
  errors,
  touched,
  isSubmitting,
  reliquidationProcess,
  setActiveComponent
}) => {
  const { balances, employees } = values;
  const [listBalances, setListBalances] = useState([]);
  const [balanceOptions, setBalanceOptions] = useState([]);
  const dispatch = useDispatch();
  const [showSpinner, setShowSpinner] = useState(false);

  const fetchBalances = () => {
    indexBalancesRequest({
      dispatch,
      params: {
        actives: true,
        reliquidation: true,
        filter_type: 'asset',
        payroll: true
      },
      successCallback: response => {
        let optionsList = [];
        const { data } = response;
        setListBalances(data.data);
        optionsList = data.data.map(item => ({ ...item, amount: '0' }));
        setBalanceOptions(optionsList);
        setFieldValue('fullObjectBalances', optionsList);
      }
    });
  };

  const getBalancesProps = balancesSelected => {
    return balanceOptions.filter(balance => balancesSelected.includes(balance.value));
  };

  const disableBalances = (balancesSelected, option) => {
    return balancesSelected.length === 1 ? !balancesSelected.includes(option.value) : false;
  };

  const existsBalances = () => {
    return balances?.length > 0;
  };

  const balanceAmount = (balance, employee) => {
    const existingEmployee = employees.find(item => item.id === employee.id);
    if (existingEmployee && existingEmployee.balances) {
      const existingBalance = existingEmployee.balances.find(item => item.value === balance.value);
      return existingBalance ? existingBalance.amount : '0';
    }
    return '0';
  };

  const clearBalances = () => {
    const updatedEmployees = employees.map(employee => ({
      ...employee,
      balances: []
    }));
    setFieldValue('employees', updatedEmployees);
    setFieldValue('balances', []);
    setFieldValue('reliquidationBalances', []);
    setShowSpinner(false);
    if (listBalances.length > 0) {
      setListBalances(
        listBalances.map(balance => ({
          ...balance,
          disabled: false
        }))
      );
    }
  };

  const handleBalances = (balancesSelected, updateEmployee = false) => {
    const balancesToUpdate = [];
    if (updateEmployee && existsBalances()) {
      balancesToUpdate.push(...balances);
    }
    if (!updateEmployee) {
      setFieldValue('balances', balancesSelected);
      balancesToUpdate.push(...balancesSelected);
    }
    setShowSpinner(true);
    indexReliquidationBalancesRequest({
      dispatch,
      params: {
        filter_reliquidation_process_ids: reliquidationProcess?.id,
        filter_employee_ids: values?.employeeIds?.map(item => item.id) || [],
        filter_balance_ids: balancesToUpdate,
        filter_assets: true
      },
      successCallback: ({ data }) => {
        const currentEmployeeBalances = data.data || [];
        const employeeBalancesObject = getBalancesProps(balancesToUpdate);
        const employeesWithBalances = employees.map(employee => {
          const employeeBalances = employeeBalancesObject.map(balance => {
            const balanceObject = currentEmployeeBalances.find(
              item => item.balance?.value === balance.value && item.employee?.id === employee.id
            );
            return {
              ...balance,
              amount: balanceObject ? balanceObject.amount : balanceAmount(balance, employee)
            };
          });
          return {
            ...employee,
            balances: [...employeeBalances]
          };
        });

        setFieldValue('employees', employeesWithBalances);

        const updatedReliquidationBalances = employeesWithBalances.map(employee => ({
          employeeId: employee.id,
          balances: employee.balances.map(balance => ({
            value: balance.value,
            amount: balance.amount
          }))
        }));
        setFieldValue('reliquidationBalances', updatedReliquidationBalances);
        setShowSpinner(false);

        if (values?.employeeIds?.length > 0 && balancesToUpdate.length > 0) {
          const limitedBalances = listBalances.map(balance => ({
            ...balance,
            disabled: disableBalances(balancesToUpdate, balance)
          }));
          setListBalances(limitedBalances);
        }
      },
      callback: () => setShowSpinner(false)
    });
  };

  useEffect(fetchBalances, []);
  useEffect(() => {
    if (existsBalances() && employees.length > 0) {
      handleBalances(balances, true);
    }
    if (balances.length === 0) {
      clearBalances();
    }
    // eslint-disable-next-line
  }, [employees.length, balances.length]);

  return (
    <Form>
      <Row className="mt-2">
        <Col />
        <Col md={1}>
          <Button
            variant="circle-dark"
            className="btn-circle mt-3"
            block
            onClick={() => {
              if (setActiveComponent) {
                setActiveComponent('ReliquidationBalancesTable');
              }
            }}
          >
            <Icon icon="chevron-back" />
          </Button>
        </Col>
        <Col md={3}>
          <Button className="mt-3" variant="primary" block type="submit" disabled={isSubmitting}>
            Guardar
          </Button>
        </Col>
      </Row>
      <Row className="align-items-center">
        <Col md={4}>
          <EmployeeFilterModal
            reliquidationProcess={reliquidationProcess}
            customParams={{
              filter_reliquidation_process: reliquidationProcess?.id
            }}
            showInput
          />
        </Col>
        <Col className="mt-4" xs={12}>
          <DualList
            es={es}
            disabled={employees.length === 0}
            filterPlaceholder="Buscar Conceptos..."
            options={listBalances}
            selected={employees.length === 0 ? [] : balances}
            name="balances"
            className="hide-all-buttons"
            onChange={selected => {
              setFieldValue('balances', selected || []);
            }}
            error={getIn(errors, 'balances')}
            touched={getIn(touched, 'balances')}
          />
        </Col>
        {showSpinner ? (
          <Col className="div-content mt-5 mb-5">
            <div className="containerSpinnerLoadDash" style={{ position: 'absolute', height: '100%' }}>
              <Spinner animation="border" variant="primary" />
            </div>
          </Col>
        ) : (
          <Col className="mb-5 mt-5">
            {employees.length <= 0 ? (
              <div style={{ height: 177, textAlign: 'center' }} className="div-center">
                No existen registros...
              </div>
            ) : (
              <ReliquidationBalancesFormTable
                employees={employees}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
              />
            )}
          </Col>
        )}
      </Row>
    </Form>
  );
};

const setInitialValues = () => {
  return {
    reliquidationBalances: [],
    balances: [], // array of balance IDs
    employees: [], // array of employee objects with balances
    employeeIds: [], // array of employee objects {id, value, label}
    fullObjectBalances: [] // full object balances
  };
};

const validationSchema = Yup.object().shape({
  employees: Yup.array()
    .of(
      Yup.object().shape({
        balances: Yup.array().of(
          Yup.object().shape({
            amount: Yup.string()
              .required('Debes ingresar un monto')
              .test('amount', 'Debes ingresar un monto valido.', value => {
                return value ? +value.replaceAll('.') > 0 : false;
              })
          })
        )
      })
    )
    .min(1, 'Debes escoger al menos un concepto.')
});

const handleSubmit = (values, { props, setSubmitting, resetForm, setFieldValue }) => {
  const { formRequest, reliquidationProcess } = props;
  const data = {
    reliquidationBalances: values.employees.map(reliquidationBalance => ({
      reliquidationProcessId: reliquidationProcess.id,
      employeeId: reliquidationBalance.id,
      balances: reliquidationBalance.balances.map(balance => ({
        balanceId: balance.value,
        balance: values.fullObjectBalances.find(fullObjectBalance => fullObjectBalance.value === balance.value),
        amount: balance.amount
      }))
    }))
  };
  formRequest(data, setSubmitting, resetForm, setFieldValue);
};

export default withFormik({
  mapPropsToValues: setInitialValues,
  validationSchema,
  handleSubmit,
  enableReinitialize: false,
  validateOnChange: false,
  validateOnMount: false,
  validateOnBlur: false
})(ReliquidationBalancesForm);
